"use client";

import React, { useState, useEffect } from 'react';
import { cn } from '@/lib/utils';
import { EditorField } from '@/config/ecoBuilder/types';
import { usePageContext } from '@/components/ecoBuilder/renderer/PageContext';
import Image from 'next/image';
import { PaddingHandle } from "@/components/ecoBuilder/editor/PaddingHandle";
import { RichTextRenderer } from '@/components/ecoBuilder/atoms/RichTextRenderer';

// ------------------------------------------------------------------
// 1. Icons (SVG)
// ------------------------------------------------------------------

const Icons = {
    facebook: (props: React.SVGProps<SVGSVGElement>) => (
        <svg viewBox="0 0 24 24" fill="currentColor" {...props}>
            <path d="M9.101 23.691v-7.98H6.627v-3.667h2.474v-1.58c0-4.085 1.848-5.978 5.858-5.978.401 0 .955.042 1.468.103a8.68 8.68 0 0 1 1.141.195v3.325a8.623 8.623 0 0 0-.653-.036c-2.148 0-2.797 1.66-2.797 3.54v.437h3.206l-.441 3.667h-2.765v7.98c-1.243.376-2.6.435-3.693.125a9.834 9.834 0 0 1-1.324-.431z" />
        </svg>
    ),
    instagram: (props: React.SVGProps<SVGSVGElement>) => (
        <svg viewBox="0 0 24 24" fill="currentColor" {...props}>
            <path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.069-4.85.069-3.204 0-3.584-.012-4.849-.069-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zm0-2.163c-3.259 0-3.667.014-4.947.072-4.358.2-6.78 2.618-6.98 6.98-.059 1.281-.073 1.689-.073 4.948 0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98 1.281.058 1.689.072 4.948.072 3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98-1.281-.059-1.69-.073-4.949-.073zm0 5.838c-3.403 0-6.162 2.759-6.162 6.162s2.759 6.163 6.162 6.163 6.162-2.759 6.162-6.163c0-3.403-2.759-6.162-6.162-6.162zm0 10.162c-2.209 0-4-1.79-4-4 0-2.209 1.791-4 4-4s4 1.791 4 4c0 2.21-1.791 4-4 4zm6.406-11.845c-.796 0-1.441.645-1.441 1.44s.645 1.44 1.441 1.44c.795 0 1.439-.645 1.439-1.44s-.644-1.44-1.439-1.44z" />
        </svg>
    ),
    tiktok: (props: React.SVGProps<SVGSVGElement>) => (
        <svg viewBox="0 0 24 24" fill="currentColor" {...props}>
            <path d="M12.525.02c1.31-.02 2.61-.01 3.91-.02.08 1.53.63 3.09 1.75 4.17 1.12 1.11 2.7 1.62 4.24 1.79v4.03c-1.44-.05-2.89-.35-4.2-.97-.57-.26-1.1-.59-1.62-.93v6.14c0 3.48-2.32 6.32-5.74 6.64-3.42.33-6.66-1.84-7.48-5.16-.83-3.32 1.23-6.83 4.54-7.48 1.26-.25 2.58-.09 3.79.4v4.13a3.54 3.54 0 0 0-2.82-.47c-1.35.34-2.3 1.55-2.3 2.95 0 1.68 1.37 3.05 3.05 3.05 1.65 0 3.02-1.32 3.05-2.97V.02z" />
        </svg>
    ),
    whatsapp: (props: React.SVGProps<SVGSVGElement>) => (
        <svg viewBox="0 0 24 24" fill="currentColor" {...props}>
            <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 0 1-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 0 1-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 0 1 2.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0 0 12.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 0 0 5.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 0 0-3.48-8.413z" />
        </svg>
    ),
    phone: (props: React.SVGProps<SVGSVGElement>) => (
        <svg viewBox="0 0 24 24" fill="currentColor" {...props}>
            <path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72 12.84 12.84 0 0 0 .7 2.81 2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45 12.84 12.84 0 0 0 2.81.7A2 2 0 0 1 22 16.92z" />
        </svg>
    ),
    youtube: (props: React.SVGProps<SVGSVGElement>) => (
        <svg viewBox="0 0 24 24" fill="currentColor" {...props}>
            <path d="M23.498 6.186a3.016 3.016 0 0 0-2.122-2.136C19.505 3.545 12 3.545 12 3.545s-7.505 0-9.377.505A3.017 3.017 0 0 0 .502 6.186C0 8.07 0 12 0 12s0 3.93.502 5.814a3.016 3.016 0 0 0 2.122 2.136c1.871.505 9.376.505 9.376.505s7.505 0 9.377-.505a3.015 3.015 0 0 0 2.122-2.136C24 15.93 24 12 24 12s0-3.93-.502-5.814zM9.545 15.568V8.432L15.818 12l-6.273 3.568z" />
        </svg>
    ),
    default: (props: React.SVGProps<SVGSVGElement>) => (
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" {...props}>
            <circle cx="12" cy="12" r="10" />
            <line x1="12" y1="8" x2="12" y2="16" />
            <line x1="8" y1="12" x2="16" y2="12" />
        </svg>
    )
};

// ------------------------------------------------------------------
// 2. Types & Schema
// ------------------------------------------------------------------

export interface SocialHeaderFooterProps {
    id: string;
    variant?: "modern_glass" | "minimal_footer" | "compact_bar" | "icons_only";
    isSticky?: boolean;
    logo?: string;
    logoText?: string;
    showLogo?: boolean;
    footerText?: string;
    socials: {
        platform: string;
        url?: string;
        active: boolean;
        customIcon?: string;
        id: string;
    }[];
    iconSize?: number;
    backgroundColor?: string;
    iconColor?: string;
    paddingTop?: number;
    paddingBottom?: number;
    isSelected?: boolean;
    onUpdateProps?: (newProps: Partial<SocialHeaderFooterProps>) => void;
}

export const SocialHeaderFooterSchema: EditorField[] = [
    {
        key: "variant",
        label: "نمط العرض",
        type: "select",
        options: [
            { value: "modern_glass", label: "نافبار عائم (زجاجي)" },
            { value: "minimal_footer", label: "فوتر بسيط (مرتكز)" },
            { value: "compact_bar", label: "شريط مكثف (تباين عالي)" },
            { value: "icons_only", label: "أيقونات فقط (توسيط)" }
        ],
        defaultValue: "modern_glass"
    },
    {
        key: "isSticky",
        label: "تثبيت المكون (Sticky)",
        type: "boolean",
        defaultValue: false,
        description: "تثبيت المكون في أعلى أو أسفل الشاشة حسب النمط"
    },
    {
        key: "showLogo",
        label: "إظهار الشعار",
        type: "boolean",
        defaultValue: true
    },
    {
        key: "logo",
        label: "صورة الشعار",
        type: "image",
    },
    {
        key: "logoText",
        label: "نص الشعار (Rich Text)",
        type: "richtext",
        defaultValue: "المتجر",
        description: "يظهر هذا النص في حال عدم اختيار صورة للشعار، يدعم التنسيق HTML"
    },
    {
        key: "footerText",
        label: "نص التذييل (الفوتر فقط)",
        type: "richtext",
        defaultValue: "© 2025 المتجر - جميع الحقوق محفوظة",
        description: "النص الذي يظهر في أسفل الفوتر، يدعم HTML."
    },
    {
        key: "socials",
        label: "روابط التواصل",
        type: "array",
        arrayConfig: {
            itemLabel: "رابط",
            schema: [
                {
                    key: "platform",
                    label: "المنصة",
                    type: "select",
                    options: [
                        { value: "facebook", label: "Facebook" },
                        { value: "instagram", label: "Instagram" },
                        { value: "tiktok", label: "TikTok" },
                        { value: "whatsapp", label: "WhatsApp" },
                        { value: "youtube", label: "YouTube" },
                        { value: "phone", label: "Phone" }
                    ],
                    defaultValue: "facebook"
                },
                {
                    key: "url",
                    label: "الرابط / الرقم",
                    type: "text",
                    placeholder: "اتركه فارغاً لجلب الرابط من إعدادات المتجر",
                    description: "للأرقام استخدم الصيغة الدولية بدون +"
                },
                {
                    key: "active",
                    label: "مفعل",
                    type: "boolean",
                    defaultValue: true
                }
            ]
        },
        defaultValue: [
            { id: 'fb', platform: 'facebook', active: true, url: '' },
            { id: 'insta', platform: 'instagram', active: true, url: '' },
            { id: 'wa', platform: 'whatsapp', active: true, url: '' },
            { id: 'ph', platform: 'phone', active: true, url: '' }
        ]
    },
    {
        key: "iconSize",
        label: "حجم الأيقونات",
        type: "slider",
        defaultValue: 20,
        sliderConfig: { min: 14, max: 60, step: 2, unit: "px" }
    },
    {
        key: 'backgroundColor',
        label: 'لون الخلفية',
        type: 'color',
    },
    {
        key: 'iconColor',
        label: 'لون الأيقونات ونص الشعار',
        type: 'color',
    },
    {
        key: "paddingTop",
        label: "مسافة علوية",
        type: "slider",
        defaultValue: 12,
        sliderConfig: { min: 0, max: 100, step: 2, unit: "px" }
    },
    {
        key: "paddingBottom",
        label: "مسافة سفلية",
        type: "slider",
        defaultValue: 12,
        sliderConfig: { min: 0, max: 100, step: 2, unit: "px" }
    }
];

export const SocialHeaderFooterDefaultProps = {
    variant: "modern_glass",
    isSticky: false,
    showLogo: true,
    logoText: "المتجر",
    footerText: "© 2025 المتجر - جميع الحقوق محفوظة",
    iconSize: 20,
    socials: [
        { id: '1', platform: 'facebook', active: true, url: '' },
        { id: '2', platform: 'instagram', active: true, url: '' },
        { id: '3', platform: 'whatsapp', active: true, url: '' },
        { id: '4', platform: 'phone', active: true, url: '' }
    ],
    paddingTop: 12,
    paddingBottom: 12
};

// ------------------------------------------------------------------
// 3. Component Implementation
// ------------------------------------------------------------------

export const SocialHeaderFooter = ({
    id,
    variant = "modern_glass",
    isSticky = false,
    logo,
    logoText,
    showLogo = true,
    footerText,
    socials = SocialHeaderFooterDefaultProps.socials,
    iconSize = 20,
    backgroundColor,
    iconColor: textColor, // Renamed iconColor to textColor as per instruction
    paddingTop = 12,
    paddingBottom = 12,
    isSelected,
    onUpdateProps
}: SocialHeaderFooterProps) => {
    const [stickyOffset, setStickyOffset] = useState(0);

    useEffect(() => {
        if (!isSticky) return;

        const checkOffset = () => {
            const bar = document.getElementById('smart-announcement-bar');
            if (bar) {
                const style = window.getComputedStyle(bar);
                // Check if bar is actually sticky or fixed
                if (style.position === 'sticky' || style.position === 'fixed') {
                    setStickyOffset(bar.offsetHeight);
                    return;
                }
            }
            setStickyOffset(0);
        };

        // Check initially and on scroll/resize
        checkOffset();
        window.addEventListener('resize', checkOffset);
        window.addEventListener('scroll', checkOffset); // Sometimes bar stickiness might change dynamically? Less likely but safe.

        return () => {
            window.removeEventListener('resize', checkOffset);
            window.removeEventListener('scroll', checkOffset);
        };
    }, [isSticky]);
    const { themeSettings } = usePageContext();

    const finalBg = backgroundColor || (
        variant === 'compact_bar' ? '#111827' :
            variant === 'modern_glass' ? 'rgba(255,255,255,0.8)' :
                variant === 'icons_only' ? 'transparent' :
                    '#ffffff'
    );

    // Default fallback depends on variant
    const defaultIconColor = variant === 'compact_bar' ? '#ffffff' : themeSettings?.primaryColor || '#000000';
    const finalIconColor = textColor || defaultIconColor;

    // Resolve Links (Mocked logic)
    const resolvedSocials = socials.filter(s => s.active).map(s => {
        let finalUrl = s.url;
        // Handle Phone protocol
        if (s.platform === 'phone' && finalUrl && !finalUrl.startsWith('tel:')) {
            finalUrl = `tel:${finalUrl}`;
        }

        return {
            ...s,
            finalUrl: finalUrl || '#'
        };
    });

    const handlePaddingChange = (side: 'top' | 'bottom') => (newVal: number) => {
        if (!onUpdateProps) return;
        if (side === 'top') onUpdateProps({ paddingTop: newVal });
        else onUpdateProps({ paddingBottom: newVal });
    };

    const renderIcon = (platform: string) => {
        const Icon = (Icons as any)[platform] || Icons.default;
        return <Icon style={{ width: iconSize, height: iconSize }} className="transition-transform hover:scale-110" />;
    };

    const renderLogo = () => {
        if (!showLogo) return null;
        const isCentered = variant === 'minimal_footer' || variant === 'icons_only';
        if (logo) {
            return (
                <div className="relative h-10 w-32">
                    <Image
                        src={logo}
                        alt={logoText || "Logo"}
                        fill
                        className={cn("object-contain", isCentered ? "object-center" : "object-right")}
                        unoptimized
                    />
                </div>
            );
        }
        return (
            <div className="text-xl font-bold tracking-tight" style={{ color: textColor }}>
                <RichTextRenderer html={logoText} />
            </div>
        );
    };

    const containerClasses = cn(
        "w-full transition-all duration-300 z-50",
        isSticky ? "sticky" : "relative", // Removed top-0 class to allow inline style override, but "sticky" class usually doesn't set top. 
        // Actually, Tailwind "sticky" sets position: sticky. "top-0" sets top: 0. 
        // We want to remove "top-0" from class and set it in style.
        isSelected ? "group" : "group"
    );

    const containerStyle = {
        backgroundColor: finalBg,
        paddingTop: `${paddingTop}px`,
        paddingBottom: `${paddingBottom}px`,
        top: (isSticky && variant !== 'minimal_footer') ? `${stickyOffset}px` : undefined,
        ...(variant === "modern_glass" ? { backdropFilter: 'blur(12px)', borderBottom: '1px solid rgba(0,0,0,0.05)' } : {}),
        ...(variant === "minimal_footer" ? { borderTop: '1px solid rgba(0,0,0,0.05)' } : {})
    };

    // ----------------------------------------------------------------
    // Variant 4: Icons Only (Centered)
    // ----------------------------------------------------------------
    if (variant === 'icons_only') {
        return (
            <div
                id={id}
                className={containerClasses}
                style={containerStyle}
                dir="rtl"
            >
                <div className="container mx-auto px-4 flex justify-center items-center flex-wrap gap-6">
                    {resolvedSocials.map((s, idx) => (
                        <a
                            key={idx}
                            href={s.finalUrl}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="hover:-translate-y-1 transition-transform duration-300 grid place-items-center"
                            style={{ color: finalIconColor }}
                        >
                            {renderIcon(s.platform)}
                        </a>
                    ))}
                </div>
                {isSelected && onUpdateProps && (
                    <>
                        <PaddingHandle side="top" currentPadding={paddingTop} onPaddingChange={handlePaddingChange('top')} />
                        <PaddingHandle side="bottom" currentPadding={paddingBottom} onPaddingChange={handlePaddingChange('bottom')} />
                    </>
                )}
            </div>
        );
    }

    // ----------------------------------------------------------------
    // Variant 2: Minimal Footer (Centered)
    // ----------------------------------------------------------------
    if (variant === 'minimal_footer') {
        return (
            <div
                id={id}
                // Override sticky logic for footer to be bottom if sticky
                className={cn(
                    "w-full transition-all duration-300 z-50",
                    isSticky ? "sticky bottom-0" : "relative",
                    isSelected ? "group" : "group"
                )}
                style={containerStyle}
                dir="rtl"
            >
                <div className="container mx-auto px-4 flex flex-col items-center gap-6">
                    {renderLogo()}

                    <div className="flex items-center justify-center gap-6 flex-wrap">
                        {resolvedSocials.map((s, idx) => (
                            <a
                                key={idx}
                                href={s.finalUrl}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="hover:-translate-y-1 transition-transform duration-300"
                                style={{ color: finalIconColor }}
                            >
                                {renderIcon(s.platform)}
                            </a>
                        ))}
                    </div>

                    <div className="text-xs opacity-50" style={{ color: finalIconColor }}>
                        <RichTextRenderer html={footerText} />
                    </div>
                </div>
                {isSelected && onUpdateProps && (
                    <>
                        <PaddingHandle side="top" currentPadding={paddingTop} onPaddingChange={handlePaddingChange('top')} />
                        <PaddingHandle side="bottom" currentPadding={paddingBottom} onPaddingChange={handlePaddingChange('bottom')} />
                    </>
                )}
            </div>
        );
    }

    // ----------------------------------------------------------------
    // Variant 3: Compact Bar (High Contrast)
    // ----------------------------------------------------------------
    if (variant === 'compact_bar') {
        return (
            <div
                id={id}
                className={containerClasses}
                style={{
                    ...containerStyle,
                    color: finalIconColor
                }}
                dir="rtl"
            >
                <div className="container mx-auto px-4 flex items-center justify-between">
                    <div className="flex items-center gap-4">
                        {resolvedSocials.map((s, idx) => (
                            <a
                                key={idx}
                                href={s.finalUrl}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="opacity-80 hover:opacity-100 transition-opacity"
                            >
                                {renderIcon(s.platform)}
                            </a>
                        ))}
                    </div>

                    {showLogo && (
                        <div className="font-bold text-sm tracking-widest uppercase opacity-90">
                            {renderLogo()}
                        </div>
                    )}
                </div>
                {isSelected && onUpdateProps && (
                    <>
                        <PaddingHandle side="top" currentPadding={paddingTop} onPaddingChange={handlePaddingChange('top')} />
                        <PaddingHandle side="bottom" currentPadding={paddingBottom} onPaddingChange={handlePaddingChange('bottom')} />
                    </>
                )}
            </div>
        );
    }

    // ----------------------------------------------------------------
    // Variant 1: Modern Glass (Navbar) - Default
    // ----------------------------------------------------------------
    return (
        <div
            id={id}
            className={containerClasses}
            style={containerStyle}
            dir="ltr"
        >
            <div className="container mx-auto px-4 flex items-center justify-between" dir="rtl">
                {/* Logo Section */}
                <div className="flex-shrink-0">
                    {renderLogo()}
                </div>

                {/* Icons Section */}
                <div className="flex items-center gap-4">
                    {resolvedSocials.map((s, idx) => (
                        <a
                            key={idx}
                            href={s.finalUrl}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="p-2 rounded-full bg-black/5 hover:bg-black/10 transition-colors flex items-center justify-center"
                            style={{ color: finalIconColor }}
                        >
                            {renderIcon(s.platform)}
                        </a>
                    ))}
                </div>
            </div>
            {/* Padding Handles */}
            {isSelected && onUpdateProps && (
                <>
                    <PaddingHandle side="top" currentPadding={paddingTop} onPaddingChange={handlePaddingChange('top')} />
                    <PaddingHandle side="bottom" currentPadding={paddingBottom} onPaddingChange={handlePaddingChange('bottom')} />
                </>
            )}
        </div>
    );
};
